/**
 * Utility functions for creating opportunities.
 * 
 * Extracted from useCreateOpportunity hook to maintain file size limits.
 */

import { SourceType } from '../shared/types/opportunity';
import { logger } from '../shared/utils/logger';

// =============================================================================
// Types
// =============================================================================

export interface ParsedListing {
  source: SourceType;
  url: string;
  extractedAddress?: string;
}

// =============================================================================
// URL Parsing
// =============================================================================

const LISTING_SOURCES: Record<string, SourceType> = {
  'zillow.com': 'zillow',
  'www.zillow.com': 'zillow',
  'redfin.com': 'redfin',
  'www.redfin.com': 'redfin',
  'realtor.com': 'realtor',
  'www.realtor.com': 'realtor',
  'landsofamerica.com': 'lands_of_america',
  'www.landsofamerica.com': 'lands_of_america',
  'landwatch.com': 'land_watch',
  'www.landwatch.com': 'land_watch',
};

/**
 * Parse a listing URL to extract source type and optional address
 */
export function parseListingUrl(url: string): ParsedListing | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    
    let source: SourceType = 'other';
    for (const [domain, sourceType] of Object.entries(LISTING_SOURCES)) {
      if (hostname.includes(domain)) {
        source = sourceType;
        break;
      }
    }
    
    // Extract address from URL path for known formats
    let extractedAddress: string | undefined;
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    
    if (source === 'zillow' && pathParts[0] === 'homedetails') {
      // Zillow: /homedetails/123-Main-St-City-State-12345/zpid
      const addressPart = pathParts[1];
      if (addressPart) {
        extractedAddress = addressPart.replace(/-/g, ' ').replace(/\d{9,}$/, '').trim();
      }
    } else if (source === 'redfin' && pathParts.length >= 3) {
      // Redfin: /CA/City/123-Main-St-12345/home/123456
      extractedAddress = pathParts[2]?.replace(/-/g, ' ').replace(/\d{5}$/, '').trim();
    } else if (source === 'lands_of_america' || source === 'land_watch') {
      // LandsOfAmerica/LandWatch: various formats, try to get from path
      const lastPath = pathParts[pathParts.length - 1];
      if (lastPath && !lastPath.match(/^\d+$/)) {
        extractedAddress = lastPath.replace(/-/g, ' ').replace(/\d+$/, '').trim();
      }
    }
    
    return { source, url: url.trim(), extractedAddress };
  } catch (err) {
    logger.warn('Failed to parse listing URL', 'create-opportunity', { url });
    return null;
  }
}

/**
 * Normalize a URL by ensuring it has a protocol
 */
export function normalizeUrl(input: string): string {
  let url = input.trim();
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  return url;
}

/**
 * Generate an opportunity name from available data
 */
export function generateOpportunityName(params: {
  inputName?: string;
  streetAddress: string | null;
  formattedAddress: string | null;
  addressToGeocode: string | null;
  parsedListing: ParsedListing | null;
  coordinates?: { latitude: number; longitude: number };
}): string {
  const { inputName, streetAddress, formattedAddress, addressToGeocode, parsedListing, coordinates } = params;

  if (inputName) return inputName;
  if (streetAddress) return streetAddress;
  if (formattedAddress) return formattedAddress;
  if (addressToGeocode) return addressToGeocode;
  
  if (parsedListing?.url) {
    try {
      const urlObj = new URL(parsedListing.url);
      return `Listing from ${urlObj.hostname.replace('www.', '')}`;
    } catch {
      // Fall through
    }
  }
  
  if (coordinates) {
    return `Pin at ${coordinates.latitude.toFixed(4)}, ${coordinates.longitude.toFixed(4)}`;
  }
  
  return 'New Opportunity';
}

